import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import axios from 'axios';

const API = `${window.location.origin}/api`;

let firebaseApp = null;
let messaging = null;

export async function initFirebaseMessaging() {
  try {
    // Fetch Firebase config from backend
    const res = await axios.get(`${API}/admin/settings/firebase/config`);
    const config = res.data;
    
    if (!config.enabled || !config.api_key) {
      console.log('Firebase push notifications not configured');
      return null;
    }

    const firebaseConfig = {
      apiKey: config.api_key,
      authDomain: config.auth_domain,
      projectId: config.project_id,
      storageBucket: config.storage_bucket,
      messagingSenderId: config.messaging_sender_id,
      appId: config.app_id,
    };

    // Initialize Firebase app (singleton)
    if (!getApps().length) {
      firebaseApp = initializeApp(firebaseConfig);
    } else {
      firebaseApp = getApps()[0];
    }

    // Register service worker and pass config
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.register('/sw.js');
      registration.active?.postMessage({
        type: 'FIREBASE_CONFIG',
        config: firebaseConfig
      });
      // Also post on controlling change
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        navigator.serviceWorker.controller?.postMessage({
          type: 'FIREBASE_CONFIG',
          config: firebaseConfig
        });
      });
    }

    messaging = getMessaging(firebaseApp);
    return { messaging, vapidKey: config.vapid_key };
  } catch (e) {
    console.log('Firebase init skipped:', e.message);
    return null;
  }
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('Browser does not support notifications');
    return null;
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.log('Notification permission denied');
    return null;
  }

  const result = await initFirebaseMessaging();
  if (!result) return null;

  try {
    const registration = await navigator.serviceWorker.getRegistration('/sw.js');
    const token = await getToken(result.messaging, {
      vapidKey: result.vapidKey,
      serviceWorkerRegistration: registration
    });
    return token;
  } catch (e) {
    console.error('Failed to get FCM token:', e);
    return null;
  }
}

export async function registerPushToken(userToken) {
  const fcmToken = await requestNotificationPermission();
  if (!fcmToken) return false;

  try {
    await axios.post(`${API}/notifications/register-push-token?token=${encodeURIComponent(fcmToken)}`, {}, {
      headers: { Authorization: `Bearer ${userToken}` }
    });
    console.log('Push token registered');
    return true;
  } catch (e) {
    console.error('Failed to register push token:', e);
    return false;
  }
}

export function onForegroundMessage(callback) {
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    
    // Play notification sound
    try {
      const audio = new Audio('/notification-sound.mp3');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (e) {
      console.log('Audio not available');
    }
    
    // Also show browser notification if permission granted
    if (Notification.permission === 'granted') {
      const { title, body, icon } = payload.notification || {};
      new Notification(title || 'HandyBayani', {
        body: body || 'You have a new notification',
        icon: icon || '/logo192.png',
        badge: '/logo192.png',
        vibrate: [200, 100, 200],
        tag: 'handybayani-foreground',
        renotify: true
      });
    }
    
    callback(payload);
  });
}
